import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { FormatId } from '../lib/formats';

// Item 97 Phase C: list endpoints opt into a {items, next_cursor,
// has_more} envelope when called with `?paginated=1`. The legacy array
// shape is preserved for callers that don't pass the flag, so the
// existing useProjects()/useRepos() hooks keep their array contract.
export interface PageEnvelope<T> {
  items: T[];
  next_cursor?: string;
  has_more: boolean;
}

// usePaginated wraps useSWRInfinite for endpoints that speak the
// envelope shape. `keyBase` is the URL up to but not including the
// pagination params; the helper appends `paginated=1`, `limit`, and
// (on pages >0) `cursor`. Passing `null` for keyBase pauses fetching
// the same way useSWR does — handy when the caller is still resolving
// auth or a route param.
export function usePaginated<T>(keyBase: string | null, limit = 50) {
  const sep = keyBase?.includes('?') ? '&' : '?';
  const swr = useSWRInfinite<PageEnvelope<T>>(
    (_idx, prev) => {
      if (!keyBase) return null;
      if (prev && !prev.has_more) return null;
      const cursor = prev?.next_cursor ?? '';
      const cursorPart = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      const tail = `paginated=1&limit=${limit}${cursorPart}`;
      return `${keyBase}${sep}${tail}`;
    },
    fetcher,
  );
  const items = swr.data ? swr.data.flatMap((p) => p.items) : [];
  const last = swr.data?.[swr.data.length - 1];
  return {
    items,
    error: swr.error,
    isLoading: swr.isLoading,
    isValidating: swr.isValidating,
    hasMore: last?.has_more ?? false,
    loadMore: () => swr.setSize(swr.size + 1),
    mutate: swr.mutate,
    size: swr.size,
  };
}

export type User = {
  email: string;
  displayName?: string;
  objectID?: string;
  tenantID?: string;
  groups?: string[];
  roles?: string[];
  // Item 139: false for a principal with no org_admin role and no
  // role_bindings row (project-scoped or org-wide) — a config-mapped
  // reader/developer role alone doesn't grant one. Lets the frontend
  // show an explanatory empty state instead of a bare empty list.
  hasAnyBinding?: boolean;
};

export type Project = {
  id: string;
  slug: string;
  name: string;
  description?: string;
};

export type Repo = {
  id: string;
  projectId: string;
  slug: string;
  name: string;
  kind: 'local' | 'remote' | 'virtual';
  format: FormatId;
  upstreamUrl?: string;
  // Ordered list of member-repo UUIDs that back a virtual repo. The
  // backend resolves them in order on pull. Empty / undefined for
  // local + remote.
  members?: string[];
};

// Patch shape for PATCH /api/v1/repos/{id}. Only fields actually sent
// move on the backend; everything else stays at its current value.
// The backend rejects changes to id and projectId; everything else
// (slug / name / kind / format / upstreamUrl / members) is editable.
export type RepoPatch = Partial<Pick<Repo,
  'slug' | 'name' | 'kind' | 'format' | 'upstreamUrl' | 'members'
>>;

async function fetcher<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    // The Accept: application/json header makes the auth middleware
    // return 401 instead of redirecting to /login when there's no
    // session — important for SWR to fail cleanly.
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export function useWhoami() {
  const { data, error, isLoading } = useSWR<User>('/auth/whoami', fetcher, {
    shouldRetryOnError: false,
  });
  return { user: data, loading: isLoading, error };
}

export function useProjects() {
  return useSWR<Project[]>('/api/v1/projects', fetcher);
}

// Item 97 Phase C: cursor-paginated variant of useProjects. The
// daily-driver Projects.tsx list switches to this so a customer with
// hundreds of projects gets a "Show more" button instead of a silent
// truncation at the legacy default of 200.
export function useProjectsPaginated(limit = 50) {
  return usePaginated<Project>('/api/v1/projects', limit);
}

export function useRepos() {
  return useSWR<Repo[]>('/api/v1/repos', fetcher);
}

export async function createProject(body: Omit<Project, 'id'>) {
  const res = await fetch('/api/v1/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<Project>;
}

export async function createRepo(body: Omit<Repo, 'id'>) {
  const res = await fetch('/api/v1/repos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<Repo>;
}

// updateRepo PATCHes an existing repo. Only the fields present in
// `patch` move; the backend leaves the rest untouched.
export async function updateRepo(id: string, patch: RepoPatch) {
  const res = await fetch(`/api/v1/repos/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<Repo>;
}

export interface RepoArtifact {
  id: string;
  path: string;
  version: string;
  size_bytes: number;
  sha256: string;
  content_type: string;
  uploaded_at: string;
  metadata: Record<string, unknown>;
}

export function useRepoDetail(id: string | undefined) {
  return useSWR<Repo>(id ? `/api/v1/repos/${id}` : null, fetcher);
}

export function useRepoArtifacts(id: string | undefined, limit = 200) {
  return useSWR<RepoArtifact[]>(
    id ? `/api/v1/repos/${id}/artifacts?limit=${limit}` : null,
    fetcher,
  );
}

// v186 — project-owner editable retention policies. The endpoint is
// gated by maintainer-or-higher; non-members get a 403 (handled by
// SWR as an error and rendered as "no permission" in the UI).
export interface RetentionRule {
  max_age_days?: number;
  keep_per_coord?: number;
}

export interface RetentionPolicy {
  id?: string;
  name: string;
  rule: RetentionRule;
  enabled: boolean;
  dry_run: boolean;
  last_run_at?: string | null;
  created_at?: string | null;
}

// useRepoRetention returns SWR-managed retention policies for a repo.
// Returns 403 → SWR error which the caller can render as "you don't
// have permission to edit retention here".
export function useRepoRetention(id: string | undefined) {
  return useSWR<RetentionPolicy[]>(
    id ? `/api/v1/repos/${id}/retention` : null,
    fetcher,
  );
}

export async function createRepoRetention(
  repoID: string,
  body: RetentionPolicy,
) {
  const res = await fetch(`/api/v1/repos/${repoID}/retention`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<RetentionPolicy>;
}

export async function updateRepoRetention(
  repoID: string,
  name: string,
  body: RetentionPolicy,
) {
  const res = await fetch(
    `/api/v1/repos/${repoID}/retention/${encodeURIComponent(name)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(await res.text());
}

export async function deleteRepoRetention(repoID: string, name: string) {
  const res = await fetch(
    `/api/v1/repos/${repoID}/retention/${encodeURIComponent(name)}`,
    {
      method: 'DELETE',
      credentials: 'include',
    },
  );
  if (!res.ok) throw new Error(await res.text());
}

// Soft-delete a repository (move to trash). project_admin or higher; the
// backend hides it immediately and the /trash view can restore it for 30 days.
export async function deleteRepo(repoID: string) {
  const res = await fetch(`/api/v1/repos/${repoID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

// Soft-delete a single artifact version (move to trash). project_admin+.
export async function deleteArtifact(artifactID: string) {
  const res = await fetch(`/api/v1/artifacts/${artifactID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

// Soft-delete a whole project + its repos (move to trash). project_admin+.
export async function deleteProject(projectID: string) {
  const res = await fetch(`/api/v1/projects/${projectID}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

// v190 — per-repo stats dashboard (roadmap item 14). Returns the
// 30-day pull sparkline, top consumers (only populated for
// maintainer+ callers — see backend), top artifacts by pull count,
// and storage totals. The endpoint is project-read gated; SWR will
// surface a 403 as `error` for repos the caller can't see.
export interface RepoStatsDailyPull {
  day: string; // YYYY-MM-DD
  count: number;
}

export interface RepoStatsTopConsumer {
  principal_email: string;
  principal_kind: string;
  count: number;
}

export interface RepoStatsTopArtifact {
  artifact_id: string;
  path: string;
  version?: string;
  sha256: string;
  size_bytes: number;
  pull_count: number;
}

export interface RepoStats {
  window_days: number;
  storage_total_bytes: number;
  storage_growth_30d: number;
  artifact_count: number;
  pulls_total_30d: number;
  pulls_30d_daily: RepoStatsDailyPull[];
  top_consumers: RepoStatsTopConsumer[];
  top_artifacts_by_pulls: RepoStatsTopArtifact[];
  cache_hit_rate_30d: number | null;
}

export function useRepoStats(id: string | undefined) {
  return useSWR<RepoStats>(
    id ? `/api/v1/repos/${id}/stats` : null,
    fetcher,
  );
}
